// Progress tracking utilities for Word of the Day
import { supabase } from "@/integrations/supabase/client";
import { syncProgressToCloud, recordGameResult } from "./cloudSync";

export interface DailyProgress {
  date: string; // YYYY-MM-DD format
  completed: boolean;
  won: boolean;
  attempts: number;
  hintsUsed: number;
  guesses: string[];
  solution: string;
  completedAt?: number; // timestamp
  revealedPositions?: Record<number, string>; // persist hint data
}

export interface GameStats {
  totalPlayed: number;
  totalWon: number;
  currentStreak: number;
  maxStreak: number;
  winPercentage: number;
  guessDistribution: Record<number, number>; // attempts -> count
}

const STORAGE_KEY = 'wormble-progress';
const STATS_KEY = 'wormble-stats';

// Get progress for a specific date
export function getDailyProgress(date: Date): DailyProgress | null {
  const dateStr = formatDateForStorage(date);
  const allProgress = getAllProgress();
  return allProgress[dateStr] || null;
}

// Save progress for a specific date
export async function saveDailyProgress(date: Date, progress: Omit<DailyProgress, 'date'>): Promise<void> {
  const dateStr = formatDateForStorage(date);
  const allProgress = getAllProgress();
  
  // Check if this is a NEW completion BEFORE saving
  const existingProgress = allProgress[dateStr];
  const isNewCompletion = !existingProgress || !existingProgress.completed;
  
  const dailyProgress: DailyProgress = {
    date: dateStr,
    ...progress
  };
  
  // Save progress to localStorage
  allProgress[dateStr] = dailyProgress;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(allProgress));
  
  // Update stats ONLY if this is a new completion
  if (progress.completed && isNewCompletion) {
    updateStatsForCompletion(dailyProgress, allProgress);
  }
  
  // Sync to cloud if user is authenticated
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.user) {
    try {
      // Use secure SECURITY DEFINER function to record game results
      // This validates stats server-side and prevents manipulation
      if (progress.completed) {
        await recordGameResult(session.user.id, date, dailyProgress);
      } else {
        // For in-progress games, still save progress normally
        await syncProgressToCloud(session.user.id, date, dailyProgress);
      }
    } catch (error) {
      console.error("Failed to sync to cloud:", error);
      // Continue with local storage even if cloud sync fails
    }
  }
}

// Get all progress data
export function getAllProgress(): Record<string, DailyProgress> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// Get game statistics
export function getGameStats(): GameStats {
  try {
    const stored = localStorage.getItem(STATS_KEY);
    return stored ? JSON.parse(stored) : {
      totalPlayed: 0,
      totalWon: 0,
      currentStreak: 0,
      maxStreak: 0,
      winPercentage: 0,
      guessDistribution: {}
    };
  } catch {
    return {
      totalPlayed: 0,
      totalWon: 0,
      currentStreak: 0,
      maxStreak: 0,
      winPercentage: 0,
      guessDistribution: {}
    };
  }
}

// Update statistics when a game is completed (called only for new completions)
function updateStatsForCompletion(progress: DailyProgress, allProgress: Record<string, DailyProgress>): void {
  const stats = getGameStats();
  
  stats.totalPlayed++;
  
  if (progress.won) {
    stats.totalWon++;
    
    // Update guess distribution
    stats.guessDistribution[progress.attempts] = 
      (stats.guessDistribution[progress.attempts] || 0) + 1;
    
    // Update streak
    stats.currentStreak = calculateCurrentStreak(allProgress);
    stats.maxStreak = Math.max(stats.maxStreak, stats.currentStreak);
  } else {
    // Reset current streak on loss
    stats.currentStreak = 0;
  }
  
  // Calculate win percentage
  stats.winPercentage = Math.round((stats.totalWon / stats.totalPlayed) * 100);
  
  localStorage.setItem(STATS_KEY, JSON.stringify(stats));
}

// Calculate current streak based on consecutive days
function calculateCurrentStreak(allProgress: Record<string, DailyProgress>): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let streak = 0;
  let checkDate = new Date(today);
  let skippedToday = false;
  
  // Check backwards from today
  while (true) {
    const dateStr = formatDateForStorage(checkDate);
    const progress = allProgress[dateStr];
    
    if (progress && progress.won) {
      streak++;
      checkDate.setDate(checkDate.getDate() - 1);
    } else if (progress && !progress.won) {
      // Game was played but not won - streak ends
      break;
    } else {
      // No game played on this date
      // Allow skipping today if user hasn't played yet
      const isToday = formatDateForStorage(checkDate) === formatDateForStorage(today);
      if (isToday && !skippedToday) {
        skippedToday = true;
        checkDate.setDate(checkDate.getDate() - 1);
        continue;
      }
      // Streak ends if we've skipped today and still no progress
      break;
    }
  }
  
  return streak;
}

// Check if a date has been completed
export function isDateCompleted(date: Date): boolean {
  const progress = getDailyProgress(date);
  return progress?.completed || false;
}

// Check if a date was won
export function isDateWon(date: Date): boolean {
  const progress = getDailyProgress(date);
  return progress?.won || false;
}

// Check if a date has an in-progress game (has guesses but not completed)
export function isDateInProgress(date: Date): boolean {
  const progress = getDailyProgress(date);
  return !!(progress && !progress.completed && progress.guesses.length > 0);
}

// Format date for consistent storage
export function formatDateForStorage(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`; // YYYY-MM-DD
}

// Get date range for archive (last N days)
export function getArchiveDateRange(days: number = 365): Date[] {
  const dates: Date[] = [];
  const today = new Date();
  const epochDate = new Date(2024, 8, 1);
  
  for (let i = 0; i < days; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    // Don't go before epoch date
    if (date >= epochDate) {
      dates.push(date);
    }
  }
  
  return dates.reverse(); // oldest first
}